import { NetworkTreeBaseDataProvider } from './NetworkTreeBaseDataProvider';
import { ConsoleRequest, DataProviderType } from '../../../utils/request';
import { DynamicTreeDataSourceOptions, NodeData } from '../../../ui/gzTree/type';
import { TreeChildrenResponse, TreeDetails, TreeNodeData } from '../types';
import {
  getViewTypeFromType,
  transformTreeStructureToParentAndChildNodes,
  treePrefixRenderer,
} from '../helpers/tree.helper';
import { ProtectedEntity } from '@nirvana/shared/dtos/types/protected-entities/common';
import { NodeChildrenResponse } from '../../networkAppContainer/helpers/types';

export class NetworkTreeDataProvider extends NetworkTreeBaseDataProvider {
  protected options: DynamicTreeDataSourceOptions = {
    cacheEnabled: false,
    requestNodeData: node => this.getNodeChildren(node),
    requestRootNodeData: (nodeId: string) => this.getNodeChildren({ nodeId: nodeId, nodeName: '' }),
    rootNodeIds: [],
    checkbox: false,
    dragAndDropEnabled: false,
    hasPagination: true,
  };

  public async getRootNode(): Promise<NodeData> {
    const response = await ConsoleRequest.createRequest<any>({
      dataProviderType: DataProviderType.CORE,
      endpoint: 'ProtectedEntitiesEPS',
      method: 'getRoot',
      payload: {},
    });

    return {
      nodeId: response._id,
      nodeName: Translator.translateProtectedEntityNameByType(
        response.name,
        response.type,
        Constants.ProductTypes.GRAVITY_ZONE_CLOUD
      ),
      originalData: {
        _id: response._id,
        type: response.type,
        path: response.path,
        parentId: null, // root node should not have a parent
        readOnly: response.readOnly,
        name: response.name,
        company: response.company,
        hasMspLicense: response.hasMspLicense,
        specifics: {
          rights: {
            manageCompanies: response.specifics?.rights?.manageCompanies,
          },
          canBeManagedByAbove: response.specifics?.canBeManagedByAbove,
          hasMspTrial: response.hasMspTrial,
          inheritedLicense: response.inheritedLicense,
        },
      },
      namePrefixRenderer: () => treePrefixRenderer(response),
    };
  }

  public async getNodeChildren(targetNode: TreeNodeData): Promise<{ currentNode: NodeData; childNodes: NodeData[] }> {
    const originalData = targetNode.originalData as ProtectedEntity;
    const viewType = getViewTypeFromType(originalData.type);
    const page = targetNode.page || 0;

    const response = await ConsoleRequest.createRequest<TreeChildrenResponse>({
      dataProviderType: DataProviderType.CORE,
      endpoint: 'ProtectedEntitiesEPS',
      method: 'getNodeChildrenNirvana',
      payload: { node: targetNode.nodeId, viewType, page },
    });

    return transformTreeStructureToParentAndChildNodes(response, targetNode);
  }

  public async getTreeNodesForChild(treeDetails: TreeDetails): Promise<NodeChildrenResponse> {
    return await ConsoleRequest.createRequest<NodeChildrenResponse>({
      dataProviderType: DataProviderType.CORE,
      endpoint: 'ProtectedEntitiesEPS',
      method: 'getNodeChildrenNirvana',
      payload: {
        node: Application.Main.getUser().company.id, // this has to be root company of user
        searchChild: {
          _id: treeDetails.targetId,
          path: treeDetails.path,
          parentId: treeDetails.parentId || '',
        },
      },
    });
  }
}
